import { Suspense } from "react";
import { useAtomValue } from "jotai";
import { useNodeId } from "@xyflow/react";
import { Icon, Text } from "@zapier/design-system";
import {
  Agent,
  assetAtomFamily_SUSPENDS,
  AssetType,
  ZapMetadata,
} from "@zapier/canvas-state";

import { isDraftOrTemplateNode } from "../../utils/isDraftOrTemplateNode";
import { ErrorBoundary } from "../ErrorBoundary";

import { UnlinkedStatusIndicator } from "./UnlinkedStatusIndicator";
import styles from "./AssetStatusIndicator.module.css";

interface AssetStatusIndicatorProps {
  assetId: string | null;
  assetType: AssetType;
}

interface AssetStatus {
  isEnabled: boolean;
  isDraft: boolean;
}

function getAssetStatus(asset: unknown, assetType: AssetType): AssetStatus {
  if (!asset) {
    return { isEnabled: false, isDraft: true };
  }

  switch (assetType) {
    case "zap": {
      const zapAsset = asset as ZapMetadata;
      return {
        isEnabled: zapAsset?.is_enabled ?? false,
        isDraft: !zapAsset?.is_published,
      };
    }
    case "agent": {
      const agentAsset = asset as Agent;
      return {
        isEnabled: agentAsset?.workflow?.enabled ?? false,
        isDraft: false, // Agents are always considered published
      };
    }
    default:
      return { isEnabled: false, isDraft: true };
  }
}

function shouldShowIndicator(
  assetType: AssetType,
  assetId: string | null,
  nodeId: string | null,
): boolean {
  const isSupportedAssetType = assetType === "zap" || assetType === "agent";
  const isDraftNodeOrTemplate = nodeId ? isDraftOrTemplateNode(nodeId) : false;
  const hasAssetId = Boolean(assetId);

  return isSupportedAssetType && !isDraftNodeOrTemplate && hasAssetId;
}

function AssetStatusIndicatorSuspended({
  assetId,
  assetType,
}: AssetStatusIndicatorProps) {
  const nodeId = useNodeId();
  const asset = useAtomValue(
    assetAtomFamily_SUSPENDS({
      assetType,
      assetId,
      interfaceProjectId: undefined,
    }),
  );

  if (!shouldShowIndicator(assetType, assetId, nodeId)) {
    return null;
  }

  const { isEnabled, isDraft } = getAssetStatus(asset, assetType);

  if (isDraft) {
    return (
      <div
        className={`${styles.container} ${styles.draft}`}
        role="status"
        data-testid="asset-indicator"
        aria-label="Asset is in draft state"
      >
        <Icon name="actionEdit" size={14} color="PrimeBlack" />
        <Text color="TextDefault" type="SmallPrint3Multiline">
          Draft
        </Text>
      </div>
    );
  }

  const statusText = isEnabled ? "On" : "Off";
  const iconColor = isEnabled ? "PrimeGreen" : "PrimeBlack";

  return (
    <div
      className={`${styles.container} ${isEnabled ? styles.enabled : styles.disabled}`}
      role="status"
      data-testid="asset-indicator"
      aria-label={`Asset status: ${statusText}`}
    >
      <Icon name="formRadioDot" size={14} color={iconColor} />
      <Text color="TextDefault" type="SmallPrint3Multiline">
        Status: {statusText}
      </Text>
    </div>
  );
}

export function AssetStatusIndicator({
  assetId,
  assetType,
}: AssetStatusIndicatorProps) {
  if (assetType && !assetId) {
    return <UnlinkedStatusIndicator />;
  }

  return (
    <ErrorBoundary renderNullOnError>
      <Suspense fallback={null}>
        <AssetStatusIndicatorSuspended
          assetId={assetId}
          assetType={assetType}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
